/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect, useRef } from "preact/hooks";
import * as d3 from "d3";
import { type Chart, type ChartSelection, CreateChart } from "../core/chart.ts";
import { AxisLayer } from "../layers/axis.ts";
import { GridLayer } from "../layers/grid.ts";
import { type LineSeries, LinesLayer } from "../layers/lines.ts";
import { type MarkerDatum, MarkerLayer } from "../layers/markers.ts";
import { RefLineLayer } from "../layers/refLine.ts";
import { SamplesLayer } from "../layers/samples.ts";
import { type ChartTickFormat } from "../layers/shared.ts";
import { TimeTickFormat } from "../utils/format.ts";
import { ChartPad, type ChartTheme, ChartUnit } from "../utils/theme.ts";
import { useChart } from "../core/useChart.ts";

export interface WaveformChartOpts {
  width?: number;
  height?: number;
  theme?: ChartTheme;
  editable?: boolean;
  zoomable?: boolean;
  maxZoom?: number;
  axis?: boolean;
  normalize?: boolean;
  color?: string;
  onSelect?: (selection: ChartSelection | null) => void;
}

export interface WaveformOverlay {
  series: LineSeries[];
  domain: [number, number];
  format?: ChartTickFormat;
}

export interface WaveformChartHandle {
  chart: Chart;
  setData(samples: ArrayLike<number> | null, sampleRate?: number): void;
  setSelection(selection: ChartSelection | null): void;
  setMarkers(markers: MarkerDatum[]): void;
  setOverlay(overlay: WaveformOverlay | null): void;
  destroy(): void;
}

export function SamplesPeak(samples: ArrayLike<number>): number {
  let peak = 1e-6;
  for (let i = 0; i < samples.length; i++) peak = Math.max(peak, Math.abs(samples[i]));
  return peak;
}

export function CreateWaveformChart(
  container: HTMLElement,
  opts: WaveformChartOpts,
): WaveformChartHandle {
  const axis = opts.axis ?? true;
  const normalize = opts.normalize ?? true;
  const samples = SamplesLayer({ color: opts.color });
  const markers = MarkerLayer();
  const overlayY = d3.scaleLinear().domain([0, 1]);
  const overlayLines = LinesLayer({ id: "overlay", y: overlayY });
  let overlay: WaveformOverlay | null = null;
  const chart = CreateChart(container, {
    width: opts.width,
    height: opts.height ?? 4 * ChartUnit,
    margin: axis
      ? { top: ChartPad, right: ChartPad, bottom: ChartPad, left: ChartPad }
      : { top: 1, right: 1, bottom: 1, left: 1 },
    x: d3.scaleLinear().domain([0, 1]),
    y: d3.scaleLinear().domain([-1, 1]),
    theme: opts.theme,
    zoomable: opts.zoomable,
    maxZoom: opts.maxZoom,
    onSelect: opts.editable ? (selection) => opts.onSelect?.(selection) : undefined,
    layers: [
      GridLayer({ axis: "x" }),
      RefLineLayer({ axis: "y", value: 0 }),
      samples,
      overlayLines,
      markers,
      ...(axis ? [AxisLayer({ side: "bottom", format: TimeTickFormat })] : []),
      AxisLayer({
        side: "right",
        inside: true,
        ticks: 3,
        scale: overlayY,
        format: (scale) => overlay?.format?.(scale) ?? scale.tickFormat(3),
        enabled: () => !!overlay,
      }),
    ],
  });

  return {
    chart,
    setData(data, sampleRate = 1) {
      samples.set(data, sampleRate);
      const duration = data && data.length ? data.length / sampleRate : 1;
      const peak = data && normalize ? SamplesPeak(data) / 0.95 : 1;
      chart.setDomain([0, duration], [-peak, peak]);
    },
    setSelection: chart.setSelection,
    setMarkers(next) {
      markers.set(next);
      chart.render();
    },
    setOverlay(next) {
      overlay = next;
      if (next) overlayY.domain(next.domain);
      overlayLines.set(next?.series ?? []);
      chart.render();
    },
    destroy: chart.destroy,
  };
}

export interface WaveformChartProps extends Omit<WaveformChartOpts, "onSelect"> {
  samples: ArrayLike<number> | null;
  sampleRate?: number;
  selection?: ChartSelection | null;
  markers?: MarkerDatum[];
  overlay?: WaveformOverlay | null;
  onSelect?: (selection: ChartSelection | null) => void;
}

export function WaveformChart(props: WaveformChartProps) {
  const onSelect = useRef(props.onSelect);
  onSelect.current = props.onSelect;

  const { ref, chart } = useChart(
    (container) =>
      CreateWaveformChart(container, {
        width: props.width,
        height: props.height,
        theme: props.theme,
        editable: props.editable,
        zoomable: props.zoomable,
        maxZoom: props.maxZoom,
        axis: props.axis,
        normalize: props.normalize,
        color: props.color,
        onSelect: (selection) => onSelect.current?.(selection),
      }),
    [
      props.width,
      props.height,
      props.theme,
      props.editable,
      props.zoomable,
      props.maxZoom,
      props.axis,
      props.normalize,
      props.color,
    ],
  );

  useEffect(() => {
    chart?.setData(props.samples, props.sampleRate);
  }, [chart, props.samples, props.sampleRate]);

  useEffect(() => {
    chart?.setMarkers(props.markers ?? []);
  }, [chart, props.markers]);

  useEffect(() => {
    chart?.setOverlay(props.overlay ?? null);
  }, [chart, props.overlay]);

  useEffect(() => {
    if (props.selection !== undefined) chart?.setSelection(props.selection);
  }, [
    chart,
    props.samples,
    props.selection?.start,
    props.selection?.end,
    props.selection === null,
  ]);

  return <div ref={ref} />;
}
